import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  BUILD_ID,
  isScrollIntendedLocked,
  getLockReasons,
  forceUnlockAll,
  enforceScrollState,
} from './scrollLockState';

// Re-export BUILD_ID for external use
export { BUILD_ID };

// Debug mode state
let debugMode = false;
let debugPanel: HTMLDivElement | null = null;
let mutationLog: string[] = [];
let lastHealthState = '';

// Get scroll health status (only called when debug panel needs update)
const getScrollHealth = () => {
  const html = document.documentElement;
  const body = document.body;
  const htmlStyles = getComputedStyle(html);
  const bodyStyles = getComputedStyle(body);

  return {
    intendedLocked: isScrollIntendedLocked(),
    lockReasons: getLockReasons(),
    html: {
      overflow: htmlStyles.overflow,
      overflowY: htmlStyles.overflowY,
      position: htmlStyles.position,
    },
    body: {
      overflow: bodyStyles.overflow,
      overflowY: bodyStyles.overflowY,
      position: bodyStyles.position,
      top: bodyStyles.top,
    },
    isBlocked: (
      htmlStyles.overflow === 'hidden' ||
      htmlStyles.overflowY === 'hidden' ||
      bodyStyles.overflow === 'hidden' ||
      bodyStyles.overflowY === 'hidden' ||
      bodyStyles.position === 'fixed'
    ),
  };
};

// Create/update debug panel
const updateDebugPanel = () => {
  if (!debugMode) return;

  if (!debugPanel) {
    debugPanel = document.createElement('div');
    debugPanel.id = 'scroll-debug-panel';
    debugPanel.style.cssText = `
      position: fixed;
      bottom: 80px;
      right: 10px;
      z-index: 99999;
      background: rgba(0, 0, 0, 0.9);
      color: #0f0;
      font-family: monospace;
      font-size: 11px;
      padding: 10px;
      border-radius: 8px;
      max-width: 320px;
      max-height: 300px;
      overflow-y: auto;
      pointer-events: auto;
    `;
    document.body.appendChild(debugPanel);
  }

  const health = getScrollHealth();
  const newState = JSON.stringify(health);

  if (newState === lastHealthState && mutationLog.length === 0) return;
  lastHealthState = newState;

  const blocked = health.isBlocked && !health.intendedLocked;

  // Clear panel safely instead of using innerHTML
  debugPanel.textContent = '';

  const createDiv = (text: string, style = '') => {
    const div = document.createElement('div');
    div.textContent = text;
    if (style) div.style.cssText = style;
    return div;
  };

  // Status header
  const statusDiv = createDiv(
    `SCROLL ${blocked ? '❌ BLOCKED' : '✅ OK'}`,
    `margin-bottom: 8px; font-weight: bold; color: ${blocked ? '#f00' : '#0f0'}`
  );
  debugPanel.appendChild(statusDiv);

  // Build info
  debugPanel.appendChild(createDiv(`Build: ${BUILD_ID}`, 'margin-bottom: 4px'));
  debugPanel.appendChild(createDiv(`Intended locked: ${health.intendedLocked}`, 'margin-bottom: 4px'));
  debugPanel.appendChild(createDiv(`Reasons: ${health.lockReasons.join(', ') || 'none'}`, 'margin-bottom: 4px'));

  // Style details
  const stylesDiv = createDiv('', 'margin-bottom: 4px; border-top: 1px solid #333; padding-top: 4px');
  const styleLines = [
    `html.overflow: ${health.html.overflow}`,
    `html.overflowY: ${health.html.overflowY}`,
    `body.overflow: ${health.body.overflow}`,
    `body.overflowY: ${health.body.overflowY}`,
    `body.position: ${health.body.position}`,
    `body.top: ${health.body.top}`,
  ];
  styleLines.forEach(line => {
    stylesDiv.appendChild(createDiv(line));
  });
  debugPanel.appendChild(stylesDiv);

  // Mutation log
  if (mutationLog.length > 0) {
    const logSection = document.createElement('div');
    logSection.style.cssText = 'border-top: 1px solid #333; padding-top: 4px; margin-top: 4px';
    logSection.appendChild(createDiv('Last mutations:', 'color: #ff0'));
    mutationLog.slice(-5).forEach(m => {
      logSection.appendChild(createDiv(m, 'font-size: 10px; color: #888'));
    });
    debugPanel.appendChild(logSection);
  }

  // Force unlock button
  const btn = document.createElement('button');
  btn.textContent = 'Force Unlock';
  btn.style.cssText = 'margin-top: 8px; padding: 4px 8px; background: #333; color: #fff; border: 1px solid #666; cursor: pointer; border-radius: 4px';
  btn.addEventListener('click', () => {
    (window as any).__forceUnlockScroll?.();
  });
  debugPanel.appendChild(btn);
};

// Initialize debug mode from URL
const initDebugMode = () => {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  debugMode = params.get('debugScroll') === '1';

  if (debugMode) {
    console.log(`[Scroll Debug] Mode enabled. Build: ${BUILD_ID}`);
    updateDebugPanel();
    startMutationObserver(); // Only start observer in debug mode
  }

  // Expose force unlock globally for debugging
  (window as any).__forceUnlockScroll = () => {
    forceUnlockAll();
    console.log('[Scroll Debug] Force unlock triggered');
  };
};

// MutationObserver - DEBUG ONLY (not started in production)
let observer: MutationObserver | null = null;

const startMutationObserver = () => {
  if (observer || !debugMode) return;

  const callback: MutationCallback = () => {
    if (!isScrollIntendedLocked()) {
      const html = document.documentElement;
      const body = document.body;
      
      const hasInlineBlock =
        html.style.overflow === 'hidden' ||
        html.style.overflowY === 'hidden' ||
        body.style.position === 'fixed' ||
        body.style.overflow === 'hidden';
      
      if (hasInlineBlock) {
        const timestamp = new Date().toLocaleTimeString();
        mutationLog.push(`${timestamp}: Blocked styles detected, fixing...`);
        if (mutationLog.length > 20) mutationLog.shift();
        
        console.warn(`[Scroll Watchdog] Unauthorized scroll lock detected, auto-fixing`);
        enforceScrollState();
      }
    }
    
    updateDebugPanel();
  };

  observer = new MutationObserver(callback);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'class'] });
  observer.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] });
};

// Ultra-minimal watchdog hook - NO intervals, NO continuous monitoring
export const useScrollWatchdog = () => {
  const location = useLocation();
  // Keep useRef for hook consistency (was used for intervals, now unused but keeps hook count stable)
  const _unused = useRef(null);

  useEffect(() => {
    // Log build ID always
    console.log(`[Build] ${BUILD_ID}`);

    // Initialize debug mode (observer only starts if ?debugScroll=1)
    initDebugMode();

    // Immediate enforce on mount
    enforceScrollState();
    
    // Catch any styles applied during initial hydration (150ms covers most component mounts)
    const hydrationTimer = setTimeout(() => {
      if (!isScrollIntendedLocked()) {
        enforceScrollState();
      }
    }, 150);
    
    // Second check for slower components (e.g., cookie consent checking localStorage)
    const slowComponentTimer = setTimeout(() => {
      if (!isScrollIntendedLocked()) {
        enforceScrollState();
      }
    }, 500);

    // Visibility change handler - direct check, no delay
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isScrollIntendedLocked()) {
        enforceScrollState();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearTimeout(hydrationTimer);
      clearTimeout(slowComponentTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Enforce on route change only
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isScrollIntendedLocked()) {
        enforceScrollState();
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [location.pathname]);
};
